import { GetStaticProps, InferGetStaticPropsType } from "next";
import Head from "next/head";
import classNames from "classnames/bind";
import StaticPageHeader from "common/blocks/StaticPageHeader";
import ReviewsSection from "common/blocks/ReviewsSection";
import RichText from "common/blocks/RichText";
import Layout from "common/layout/Layout";
import baseModel, { ResolvedBaseModel } from "models/baseModel";
import {
    InvestorAnnouncements,
    SharePrice,
    StaticPage,
    StaticPageComponentWrapper,
} from "services/cms/models";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import { IContentItem, ITaxonomyTerms } from "services/content-manager/models";
import getStaticPageContentByPath from "services/cms/getStaticPageContentByPath";
import getReviewsSection from "services/cms/getReviewsSection";
import { default as getTrustpilotReviews } from "services/trustpilot/getReviews";
import staticPageResolver, { ResolvedStaticPageContent } from "resolvers/pages/staticPageResolver";
import trustpilotResolver, { ResolvedReviews } from "resolvers/data/trustpilotResolver";
import reviewsSectionResolver, {
    ResolvedReviewsSection,
} from "resolvers/data/reviewsSectionResolver";
import { INVESTOR_ANNOUNCEMENTS_URL, SHARE_PRICE_URL, REVALIDATE_SECONDS } from "lib/constants";
import styles from "./StaticPage.module.scss";
import { RelatedArticlesComponent } from "services/cms/models/relatedArticlesComponent";
import getRelatedArticlesForComponent from "services/cms/getRelatedArticlesForComponent";
import { composeFullTaxonomyPaths } from "utils/composeFullTaxonomy";
import { SharePriceModel, SharePriceResponse } from "models/sharePriceModel";
import axios from "axios";
import dayjs from "utils/dayjs";
import { InvestorAnnouncementModel } from "models/investorAnnouncementsModel";
import { useEffect } from "react";
import useStructuredDataContext from "common/providers/structuredData/useStructuredDataContext";
import { StaticBreadcrumbs } from "common/blocks/StaticBreadcrumbs";

const cx = classNames.bind(styles);

export default function Page(props: InferGetStaticPropsType<typeof getStaticProps>) {
    const { createStaticPageStructuredData } = useStructuredDataContext();

    useEffect(() => {
        if (props.structuredDataSchema) createStaticPageStructuredData(props);
    }, [createStaticPageStructuredData, props]);

    return (
        <Layout {...props}>
            <Head>
                <meta property="og:type" content="website" />
            </Head>
            <StaticPageHeader
                title={props.pageHeading}
                breadcrumbText={props.breadcrumbText}
                pageNavigation={props.pageNavigation}
            ></StaticPageHeader>
            <div className={cx("container")}>
                <div id="page-content" className={cx("content")}>
                    <RichText
                        {...props.body}
                        props={{
                            ...props.reviews,
                            faq: {
                                isAlternateTheme: true,
                                isStaticPage: true,
                            },
                        }}
                        isStaticPage
                        isAlternateTheme
                        className={cx("richText")}
                    />
                </div>
            </div>
            {props.includeReviews && props.reviews && (
                <ReviewsSection
                    title={props.reviewsSection.title}
                    trustpilot={props.reviews.trustpilot}
                    isTrustpilotBadge={props.reviewsSection.trustpilotReviews}
                />
            )}
        </Layout>
    );
}

export const getStaticPaths = async () => {
    const paths = await composeFullTaxonomyPaths();

    return {
        paths,
        fallback: "blocking",
    };
};

export const getStaticProps: GetStaticProps<
    ResolvedStaticPageContent &
        ResolvedBaseModel & {
            reviews: {
                trustpilot: ResolvedReviews;
            };
            reviewsSection: ResolvedReviewsSection;
        },
    { segment: string[] }
> = async ({ params, preview }) => {
    if (!params) {
        return {
            notFound: true,
        };
    }

    const { segment } = params;

    const [staticPageContent, trustpilotReviews, reviewsSectionData, resolvedBaseModel] =
        await Promise.all([
            getStaticPageContentByPath(segment, preview),
            getTrustpilotReviews(),
            getReviewsSection(preview),
            baseModel(preview),
        ]);

    if (staticPageContent && resolvedBaseModel) {
        await substituteRelatedArticlesComponent(staticPageContent, preview);
        await getQ4europeData(staticPageContent);
        const resolvedStaticPageContent = await staticPageResolver(
            staticPageContent,
            segment,
            preview
        );
        const trustpilot = trustpilotResolver(trustpilotReviews);
        const reviewsSection = reviewsSectionResolver(reviewsSectionData);

        resolvedBaseModel.baseData.footer.hideFooterSignupForm =
            resolvedStaticPageContent.hideFooterSignupForm;

        return {
            props: {
                ...resolvedBaseModel,
                ...resolvedStaticPageContent,
                reviews: {
                    trustpilot,
                },
                reviewsSection,
            },
            revalidate: REVALIDATE_SECONDS.DEFAULT,
        };
    }

    return {
        notFound: true,
    };
};

async function substituteRelatedArticlesComponent(
    staticPageContent: StaticPage,
    preview?: boolean
) {
    // If we have any linked items of type 'Related Articles Component', use that data to load in the required data for the article cards
    // Then alter the data in the static page body so 'Related Articles Component' becomes a 'Related Articles' type
    const bodyLinkedItems = staticPageContent.elements.body.linkedItems as IContentItem[];
    if (
        bodyLinkedItems.some(
            (linkedItem) => linkedItem.system.type === ContentTypeCodenames.RelatedArticlesComponent
        )
    ) {
        const relatedArticlesComponents = bodyLinkedItems.filter(
            (linkedItem) => linkedItem.system.type === ContentTypeCodenames.RelatedArticlesComponent
        ) as RelatedArticlesComponent[];
        for (const relatedArticlesComponent of relatedArticlesComponents) {
            const relatedArticlesCardData = await getRelatedArticlesForComponent(
                relatedArticlesComponent,
                preview
            );
            if (relatedArticlesCardData) {
                const index = bodyLinkedItems.indexOf(relatedArticlesComponent);
                staticPageContent.elements.body.linkedItems[index].elements = {
                    heading: relatedArticlesComponent.elements.heading,
                    articles: { linkedItems: relatedArticlesCardData },
                    cta: relatedArticlesComponent.elements.cta,
                };
                staticPageContent.elements.body.linkedItems[index].system.type = "related_articles";
            }
        }
    }
}

async function getQ4europeData(staticPageContent: StaticPage) {
    // If we have any linked items of type 'Static Page Component Wrapper' and if any child items are of type 'Investor Announcements' or 'Share Price'
    // Then we need to load the data for those components on the server, to ensure the data is cached and not load the data on the client and cause layout shift
    const bodyLinkedItems = staticPageContent.elements.body.linkedItems as IContentItem[];
    const staticPageComponentWrapperComponents = bodyLinkedItems.filter(
        (linkedItem) => linkedItem.system.type === ContentTypeCodenames.StaticPageComponentWrapper
    ) as StaticPageComponentWrapper[];
    if (staticPageComponentWrapperComponents.length > 0) {
        for (const staticPageComponentWrapperComponent of staticPageComponentWrapperComponents) {
            const componentLinkedItems = staticPageComponentWrapperComponent.elements.components
                .linkedItems as IContentItem[];

            const sharePriceItems = componentLinkedItems.filter(
                (item) => item.system.type === ContentTypeCodenames.SharePrice
            ) as SharePrice[];

            if (sharePriceItems.length > 0) {
                for (const sharePriceItem of sharePriceItems) {
                    sharePriceItem.elements.data = await getSharePriceData();
                }
            }

            const announcementItems = componentLinkedItems.filter(
                (item) => item.system.type === ContentTypeCodenames.InvestorAnnouncements
            ) as InvestorAnnouncements[];

            if (announcementItems.length > 0) {
                for (const announcementItem of announcementItems) {
                    announcementItem.elements.data = await getInvestorAnnouncementsData();
                }
            }
        }
    }
}

const getSharePriceData = async (): Promise<SharePriceModel> => {
    const sharePriceResponse = await axios.get<SharePriceResponse>(SHARE_PRICE_URL, {
        headers: {
            "x-q4-client-integration": "7421ea50-937c-45ea-9947-7e14c2e187c6",
        },
    });
    const { Change, TradePrice, PercChange, TradeDate } =
        sharePriceResponse.data.GetFullStockQuoteListResult[0];

    const changeType = Change < 0 ? "negative" : Change > 0 ? "positive" : "none";

    return {
        changeType: changeType,
        lastTradePrice: TradePrice.toFixed(2),
        change: Change.toFixed(2),
        changePercent: `${changeType === "positive" ? "+" : ""}${PercChange.toFixed(2)}`,
        tradeTimestamp: dayjs(TradeDate).tz("Europe/London").format("h:mm A"),
    };
};

const getInvestorAnnouncementsData = async (): Promise<InvestorAnnouncementModel[]> => {
    const investorAnnouncementsResponse = await axios.get(INVESTOR_ANNOUNCEMENTS_URL);
    return investorAnnouncementsResponse.data.data as InvestorAnnouncementModel[];
};
